import AvailabilityDay from './availability-day';
import AvailabilityQueue from './availability-queue';
import moment from 'moment-timezone';

// stores and retrieves AvailabilityDay instances
export default class AvailabilityCache {

  static get defaults() {
    return {
      // max horizon to load (in days)
      horizon: 60,
      // max batch size
      batchSize: 5,
      // max concurrent requests
      concurrency: 3,
      // number of days to preload
      preload: 12,
      // IANA timezone
      timezone: 'America/New_York',
      // place_id
      place_id: null
    };
  }

  /**
   * AvailabilityCache constructor
   * @method constructor
   * @param  {Object}    [opts={}] – see `defaults`
   * @return {AvailabilityCache}
   */

  constructor( opts = {} ) {
    this._options = Object.assign( {}, AvailabilityCache.defaults, opts );

    this._createDays();
    this._createQueue();
  }

  /**
   * Prime the cache with an AvailabilityDay for each day before the horizon
   * @method _createDays
   * @return {undefined}
   */

  _createDays() {
    this._availability = Object.create( null );

    const tz = this._options.timezone;

    // zeroed out to beginning of the day in user's timezone
    const start = moment.tz( moment().format('YYYY-MM-DD'), tz );

    for ( let i = 0; i < this._options.horizon; ++i ) {
      const date = start.clone().add( i, 'day' ).toDate();
      const opts = Object.assign( {}, this._options );
      const val  = new AvailabilityDay( date, opts );

      this._availability[ val.key ] = val;
    }
  }

  /**
   * Create an AvailabilityQueue
   * @method _createQueue
   * @return {undefined}
   */

  _createQueue() {
    const opts = Object.assign( {}, this._options );
    this._queue = new AvailabilityQueue( opts );
  }

  /**
   * Start pre-fetching
   * @method start
   * @return {undefined}
   */

  start() {
    const preload = this._options.preload;
    this.list().slice( 0, preload ).forEach( day => this._queue.lazy( day ) );
  }

  /**
   * Get an array of all AvailabilityDay objects
   * @method list
   * @return {Array<AvailabilityDay>}
   */

  list() {
    const target = this._availability;
    return Object.keys( target ).map( key => target[ key ] );
  }

  /**
   * Get an AvailabilityDay based on its date key
   * @method get
   * @param  {String} key – date key (e.g. 'Tue May 14 2019')
   * @return {AvailabilityDay}
   */

  get( key ) {
    return this._availability[ key ];
  }

  /**
   * Load slots for the given key
   * @method load
   * @param  {String} key – cache key (e.g. 'Tue May 14 2019')
   * @return {Promise<Array<AvailabilitySlot>>}
   */

  load( key ) {
    const day = this.get( key );

    if ( !day ) {
      return Promise.reject( new Error('Out of bounds') );
    }

    if ( day.isLoading() ) {
      return day._promise;
    }

    if ( day.isLoaded() ) {
      return Promise.resolve( day.slots );
    }

    return this._queue.urgent( day );
  }

  /**
   * Reset the cache
   * @method reset
   */

  reset() {
    this.list().forEach( day => day.reset() );
    this._queue.flush();
    this.start();
  }

  /**
   * Get the cache key for a given date...
   * Rounds to the beginning of the day in the given timeone
   * @method keyFor
   * @param  {String|Date|Number} date – input date
   * @param  {String}             tz   – IANA timezone string
   * @return {String}
   */

  static keyFor( date, tz ) {
    const rounded = moment.tz( moment( date ).format('YYYY-MM-DD'), tz );
    return rounded.format('YYYY-MM-DD');
  }

}
